import React, { useEffect, useState } from 'react';
import { Send, Trash2, ThumbsUp, User as UserIcon, Clock } from 'lucide-react';
import { useUser } from '../UserContext';
import { db } from '../Firebase';
import { collection, addDoc, serverTimestamp, onSnapshot, orderBy, query, deleteDoc, doc, updateDoc, arrayUnion, arrayRemove, increment } from 'firebase/firestore';
import { Link } from 'react-router-dom';

function formatTime(ts) {
  if (!ts) return 'Just now';
  try {
    const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return date.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' });
  } catch {
    return 'Just now';
  }
}

export default function CommunityForumPage() {
  const { currentUser, userData } = useUser();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'forumPosts'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPosts(items);
    });
    return () => unsub();
  }, []);

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    if (newPost.trim() === '' || !currentUser) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'forumPosts'), {
        uid: currentUser.uid,
        author: userData?.name || currentUser.displayName || 'Anonymous',
        content: newPost.trim(),
        likes: 0,
        likedBy: [],
        createdAt: serverTimestamp(),
        email: currentUser.email || null,
      });
      setNewPost('');
    } catch (err) {
      console.error('Failed to post', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (postId, uid) => {
    if (!currentUser || currentUser.uid !== uid) return;
    try {
      await deleteDoc(doc(db, 'forumPosts', postId));
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };
  const toggleLike = async (post) => {
    if (!currentUser) return;
    const { id, likedBy = [], likes = 0 } = post;
    const hasLiked = likedBy.includes(currentUser.uid);
    try {
      const ref = doc(db, 'forumPosts', id);
      if (hasLiked) {
        await updateDoc(ref, {
          likedBy: arrayRemove(currentUser.uid),
          likes: increment(-1),
        });
      } else {
        await updateDoc(ref, {
          likedBy: arrayUnion(currentUser.uid),
          likes: increment(1),
        });
      }
    } catch (err) {
      console.error('Failed to toggle like', err);
    }
  };
  return (
    <div className="container mx-auto px-4 pb-32 mt-3" >

      <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">Community Forum</h1>
      <p className="text-center text-gray-600 mb-10">Share knowledge, ask questions, and connect with your peers.</p>

      <div className="space-y-6">
        {posts.map(post => {
          const isMine = currentUser && post.uid === currentUser.uid;
          const hasLiked = currentUser ? (post.likedBy || []).includes(currentUser.uid) : false;
          return (
            <div
              key={post.id}
              className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-xl bg-white p-4 rounded-lg shadow-md border relative ${isMine ? 'bg-green-50 border-green-200' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start">
                    <div className={`w-8 h-8 rounded-full mr-3 flex items-center justify-center ${isMine ? 'bg-green-200 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                      <UserIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-800">{post.author}</p>
                       
                      </div>
                      <p className="text-gray-700 mt-1 whitespace-pre-wrap">{post.content}</p>
                      
                    </div>
                    
                  </div>
                  {isMine && (
                    <button onClick={() => handleDelete(post.id, post.uid)} className="text-gray-400 hover:text-red-500">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <div className={`mt-3 flex items-center ${isMine ? 'justify-end' : 'justify-start'}`}>
                  <button
                    className={`flex items-center ${hasLiked ? 'text-green-600' : 'text-gray-600'} hover:text-green-600 disabled:opacity-60`}
                    onClick={() => toggleLike(post)}
                    disabled={!currentUser}
                  >
                    <ThumbsUp className={`w-5 h-5 mr-2 ${hasLiked ? 'fill-green-600' : ''}`} /> {post.likes || 0}
                  </button>
                  <span className="text-xs text-gray-500 flex items-center gap-1 ml-5">
                          <Clock className="w-3 h-3" /> {formatTime(post.createdAt)}
                  </span>
                </div>

              </div>
            </div>
          );
        })}

      </div>

      {/* Fixed bottom form */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="container mx-auto px-4 py-4">
          <form onSubmit={handlePostSubmit} className="flex flex-col sm:flex-row items-end gap-3">
            <textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none w-full"
              rows="1"
              placeholder={currentUser ? 'Share a success story or ask a question...' : 'Log in to post a message'}
              disabled={!currentUser || submitting}
            ></textarea>

            <button
              type="submit"
              disabled={!currentUser || submitting || newPost.trim() === ''}
              className="w-full sm:w-auto bg-green-600 disabled:bg-green-300 text-white font-bold py-3 px-6 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center h-12 sm:h-16 shrink-0"
            >
              <Send className="w-4 h-4 mr-2" /> {submitting ? 'Posting...' : 'Post'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}


